import { useId, useRef, useState } from 'react';
import { differenceInDays } from 'date-fns';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  Label,
  LabeledControl,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { formatDate } from '../../../../utils/formatters';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';
import type { PickableDocument } from '../../../../utils/incomeDocuments';
import { EXPIRING_SOON_DAYS } from '../../../../constants/incomeDocs';

function isExpiringSoon(expirationDateTime: string | null | undefined) {
  return !!expirationDateTime && differenceInDays(new Date(expirationDateTime), new Date()) < EXPIRING_SOON_DAYS;
}

export type IdentifySavedDocumentsProps = {
  typeLabel: string;
  candidates: Array<PickableDocument>;
  onAdd: (documentIds: Array<string>) => void;
  maxSelectable?: number;
  focusHeading: () => void;
};

export function IdentifySavedDocuments({
  typeLabel,
  candidates,
  onAdd,
  maxSelectable = Infinity,
  focusHeading,
}: IdentifySavedDocumentsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const candidateIds = new Set(candidates.map(({ id }) => id));
  // A refetch can take a file off the list while the panel is open
  const selected = selectedIds.filter((id) => candidateIds.has(id));

  const toggle = (documentId: string) =>
    setSelectedIds((current) =>
      current.includes(documentId) ? current.filter((id) => id !== documentId) : [...current, documentId],
    );

  const close = () => {
    setSelectedIds([]);
    setIsOpen(false);
  };

  const cancel = () => {
    close();
    triggerRef.current?.focus();
  };

  const add = () => {
    onAdd(selected);
    close();
    focusHeading();
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
      <TextButton
        ref={triggerRef}
        type="button"
        textStyle="body-sm-bold"
        icon={isOpen ? <IconChevronUpFilled /> : <IconChevronDownFilled />}
        iconPosition="end"
        aria-expanded={isOpen}
        aria-controls={panelId}
        css={{ alignSelf: 'flex-start' }}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
      >
        {`Identify saved ${typeLabel}`}
      </TextButton>
      {isOpen && (
        <Card
          id={panelId}
          elevated
          css={{ paddingBlockStart: 'tightest' }}
          body={
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
              <Box
                // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a FieldSet legend renders no legend in Constellation 10
                role="group"
                aria-label="Saved files"
                css={{ display: 'flex', flexDirection: 'column' }}
              >
                {candidates.map(({ id, fileName, expirationDateTime }) => (
                  <LabeledControl
                    key={id}
                    label={
                      <Label
                        css={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'baseline',
                          columnGap: 'tighter',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {fileName}
                        {isExpiringSoon(expirationDateTime) && (
                          <Text textStyle="body-sm" color="text.subtle">
                            {`Expires ${formatDate(expirationDateTime)}`}
                          </Text>
                        )}
                      </Label>
                    }
                    control={
                      <Checkbox
                        checked={selected.includes(id)}
                        disabled={!selected.includes(id) && selected.length >= maxSelectable}
                        onChange={() => toggle(id)}
                      />
                    }
                  />
                ))}
              </Box>
              <ButtonGroup aria-label={`Identify saved ${typeLabel} actions`} css={{ justifyContent: 'flex-end' }}>
                <TextButton type="button" onClick={cancel}>
                  Cancel
                </TextButton>
                <Button type="button" size="sm" tone="neutral" disabled={selected.length === 0} onClick={add}>
                  {selected.length === 0
                    ? 'Add selected files'
                    : `Add ${selected.length} selected ${selected.length === 1 ? 'file' : 'files'}`}
                </Button>
              </ButtonGroup>
            </Box>
          }
        />
      )}
    </Box>
  );
}
